import { APP_NAME } from "../config.js";
import type { SourceInfo } from "./source-info.js";

export type SlashCommandSource = "extension" | "prompt" | "skill";

export interface SlashCommandInfo {
	name: string;
	description?: string;
	source: SlashCommandSource;
	sourceInfo: SourceInfo;
}

export interface BuiltinSlashCommand {
	name: string;
	description: string;
}

export const BUILTIN_SLASH_COMMANDS: ReadonlyArray<BuiltinSlashCommand> = [
	{ name: "help", description: "Show help with example prompts and useful commands" },
	{ name: "skills", description: "List loaded skills, or install from registry: /skills install <name>" },
	{ name: "init", description: "Set up Elyra for this project (.elyra/ directory, extensions, blueprints)" },
	{ name: "settings", description: "Open settings menu" },
	{ name: "model", description: "Select model (opens selector UI)" },
	{ name: "thinking", description: "Set thinking level for the current model (opens selector, or /thinking <level>)" },
	{ name: "scoped-models", description: "Enable/disable models for Ctrl+P cycling" },
	{ name: "export", description: "Export session (HTML default, or specify path: .html/.jsonl)" },
	{ name: "import", description: "Import and resume a session from a JSONL file" },
	{ name: "share", description: "Share session as a secret GitHub gist" },
	{ name: "copy", description: "Copy last agent message to clipboard" },
	{ name: "name", description: "Set session display name" },
	{ name: "session", description: "Show session info and stats" },
	{ name: "changelog", description: "Show changelog entries" },
	{ name: "bug", description: "Report a problem: /bug <description> opens a prefilled GitHub issue" },
	{
		name: "system",
		description:
			"Add a system instruction from this point on: /system <text> (persisted, applies to following turns)",
	},
	{ name: "hotkeys", description: "Show all keyboard shortcuts" },
	{ name: "fork", description: "Create a new fork from a previous user message" },
	{ name: "clone", description: "Duplicate the current session at the current position" },
	{ name: "tree", description: "Navigate session tree (switch branches)" },
	{ name: "rewind", description: "Rewind session and file state to a previous point" },
	{ name: "login", description: "Configure provider authentication" },
	{ name: "logout", description: "Remove provider authentication" },
	{ name: "new", description: "Start a new session" },
	{ name: "compact", description: "Manually compact the session context" },
	{ name: "resume", description: "Resume a different session" },
	{ name: "cost", description: "Show session cost and token usage" },
	{
		name: "diff",
		description: "Show changes (git diff). Flags: --session, --cached, --editor (open in $EDITOR), or a path",
	},
	{
		name: "review",
		description:
			"Get an independent code review of your changes. Flags: --cross (review with a different vendor's model for a true second opinion), --session, or a path",
	},
	{
		name: "bisect",
		description:
			"Find which agent turn introduced a problem. /bisect <command> runs it automatically (like git bisect run)",
	},
	{ name: "pin", description: "Pin a file to context (survives compaction): /pin <path>" },
	{ name: "memory", description: "View or manage project memory" },
	{ name: "unpin", description: "Unpin a file from context: /unpin <path>" },
	{ name: "pins", description: "List pinned context files" },
	{ name: "blueprint", description: "Apply a session blueprint: /blueprint [name]" },
	{ name: "snippet", description: "Send a saved instruction snippet: /snippet [name]" },
	{ name: "theme", description: "Switch color theme (opens selector, or /theme <name>)" },
	{ name: "footer", description: "Toggle minimal footer (hide token stats and context usage)" },
	{ name: "reload", description: "Reload keybindings, extensions, skills, prompts, and themes" },
	{ name: "packages", description: "Browse and install available extensions" },
	{ name: "ext", description: "Browse and install available extensions (alias for /packages)" },
	{
		name: "goal",
		description: "Set a goal: agent keeps working until the command exits with code 0 (--budget <usd> caps spend)",
	},
	{ name: "learn", description: "Extract a reusable skill from the current session" },
	{
		name: "decisions",
		description:
			"Browse the project decision log (/decisions [id] to show one, /decisions off|confirm|auto to set mode)",
	},
	{ name: "replay", description: "Replay the last turn on a new branch with a different model" },
	{ name: "route", description: "Show what smart routing would do for the next turn" },
	{ name: "routing-stats", description: "Show local per-model outcome measurements from the routing ledger" },
	{ name: "update", description: "Update Elyra and installed extensions to the latest version and restart" },
	{ name: "quit", description: `Quit ${APP_NAME}` },
];
